<template>
  <v-container fluid class="home py-0">
    <v-navigation-drawer
      v-model="isMenuOpen"
      temporary
      right
      width="320"
      class="home__drawer"
    >
      <div class="pa-6">
        <div class="d-flex justify-space-between align-center mb-8">
          <p class="mb-0 text-uppercase text-neutra-bold">{{ $t('menu') }}</p>
          <button
            type="button"
            class="home__icon-button text-neutra-book"
            aria-label="Fermer le menu"
            @click="isMenuOpen = false"
          >
            x
          </button>
        </div>

        <button
          v-for="link in navigationLinks"
          :key="link.id"
          type="button"
          class="home__drawer-link text-neutra-book"
          @click="navigateTo(link.id)"
        >
          {{ link.label }}
        </button>

        <v-btn
          outlined
          class="home__inquiry-btn mt-8"
          @click="openInquiryModal"
        >
          {{ $t('requestConsultation') }}
        </v-btn>
        <button
          type="button"
          class="home__drawer-client-access text-neutra-book d-inline-block d-md-none"
          @click="openClientAccess"
        >
          {{ $t('clientAccess') }}
        </button>
      </div>
    </v-navigation-drawer>

    <v-dialog
      v-model="isInquiryOpen"
      fullscreen
      transition="dialog-bottom-transition"
    >
      <v-card class="home__inquiry-modal">
        <div class="home__inquiry-modal-content">
          <div class="d-flex justify-space-between align-center mb-8">
            <p class="mb-0 home__inquiry-title text-neutra-bold">
              {{ $t('privateInquiryTitle') }}
            </p>
            <button
              type="button"
              class="home__icon-button text-neutra-book"
              aria-label="Fermer la demande privee"
              @click="closeInquiryModal"
            >
              x
            </button>
          </div>

          <p class="home__inquiry-subtitle text-neutra-book mb-8">
            {{ $t('privateInquirySubtitle') }}
          </p>

          <v-form ref="inquiryForm" @submit.prevent="submitInquiryForm">
            <div class="home__inquiry-field">
              <label class="home__inquiry-field-label text-neutra-book" for="inquiry-name">
                {{ $t('inquiryName') }}
              </label>
              <input
                id="inquiry-name"
                v-model.trim="inquiryName"
                type="text"
                class="home__inquiry-input text-neutra-book"
                autocomplete="name"
                required
              >
            </div>
            <div class="home__inquiry-field">
              <label class="home__inquiry-field-label text-neutra-book" for="inquiry-phone">
                {{ $t('inquiryPhone') }}
              </label>
              <input
                id="inquiry-phone"
                v-model.trim="inquiryPhone"
                type="tel"
                class="home__inquiry-input text-neutra-book"
                autocomplete="tel"
                required
              >
            </div>
            <div class="home__inquiry-field">
              <label class="home__inquiry-field-label text-neutra-book" for="inquiry-referral">
                {{ $t('inquiryReferralCode') }}
              </label>
              <input
                id="inquiry-referral"
                v-model.trim="inquiryReferralCode"
                type="text"
                class="home__inquiry-input text-neutra-book"
                autocomplete="off"
              >
            </div>

            <p v-if="inquiryFeedback" class="mb-5 text-neutra-book text-granate">
              {{ inquiryFeedback }}
            </p>

            <div class="d-flex align-center">
              <v-btn
                outlined
                class="home__inquiry-submit"
                type="submit"
                :loading="inquirySubmitting"
                :disabled="inquirySubmitting"
              >
                {{ $t('sendCard') }}
              </v-btn>
            </div>
          </v-form>
        </div>
      </v-card>
    </v-dialog>

    <v-row class="home__navbar">
      <v-col
        cols="12"
        md="4"
        class="pb-0"
      >
        <v-img
          :src="require('../assets/logo.svg')"
          class="home__navbar--logo"
          eager
          contain
        />
      </v-col>
      <v-col
        cols="12"
        md="8"
        class="home__navbar-controls d-flex justify-end align-center pb-0 flex-wrap"
      >
        <button
          type="button"
          class="home__header-control home__header-control--menu text-neutra-book"
          :aria-label="$t('menu')"
          @click="isMenuOpen = true"
        >
          <span class="home__menu-icon" aria-hidden="true">
            <span />
            <span />
            <span />
          </span>
        </button>
        <button
          type="button"
          class="home__header-control home__header-control--vault text-neutra-book d-none d-md-inline-flex"
          :aria-label="$t('clientAccess')"
          @click="openClientAccess"
        >
          <span class="home__access-icon" aria-hidden="true">
            <span class="home__access-icon-shackle" />
            <span class="home__access-icon-body" />
          </span>
        </button>
        <button
          type="button"
          class="home__header-control home__header-control--cta text-neutra-book"
          @click="openInquiryModal"
        >
          {{ $t('requestConsultation') }}
        </button>
      </v-col>
    </v-row>

    <v-row class="home__title" data-reveal>
      <v-col
        cols="12"
        md="10"
        offset-md="1"
      >
        <div class="home__hero text-center">
          <p class="home__hero-kicker text-neutra-book mb-3">{{ $t('heroKicker') }}</p>
          <p class="home__hero-line text-center text-neutra-bold mb-3">{{ $t('title1') }}</p>
          <p class="home__hero-subline text-center text-neutra-book mb-5">{{ $t('title2') }}</p>
          <p class="home__hero-description text-neutra-book mb-7">
            <span v-html="$t('heroDescription')" />
          </p>
        </div>
      </v-col>
    </v-row>

    <v-row class="home__services px-md-5 px-lg-12">
      <v-col cols="12" class="d-none d-md-block" data-reveal>
        <p class="text-center home__services--title text-neutra-bold text-uppercase">
          {{ $t('services') }}
        </p>
      </v-col>
      <v-col
        v-for="(service, index) in services"
        :id="service.id"
        :key="index"
        cols="12"
        class="home__service-card"
        data-reveal
      >
        <div class="home__service-editorial">
          <div class="home__service-editorial-media">
            <div class="home__service-card--badge home__service-card--badge-overlay d-inline-block">
              <p class="mb-0 text-neutra-bold text-uppercase">{{ service.badge }}</p>
            </div>
            <v-img
              :src="service.img"
              alt="home"
              loading="lazy"
              class="home__service-card--image home__image-system home__image-system--service"
            />
          </div>
          <div class="home__service-editorial-copy">
            <p class="text-neutra-bold home__service-card--title mb-3">
              {{ service.description.title }}
            </p>
            <p
              v-if="service.description.subtitle1"
              class="text-neutra-book home__service-card--subtitle home__editorial-lead pt-3"
              v-html="service.description.subtitle1"
            />
            <p
              v-if="service.description.subtitle2"
              class="text-neutra-book home__service-card--subtitle pt-3"
              v-html="service.description.subtitle2"
            />
            <p
              v-if="service.description.subtitle3"
              class="text-neutra-book home__service-card--subtitle pt-3"
              v-html="service.description.subtitle3"
            />
            <div class="mt-6">
              <v-btn
                outlined
                class="home__inquiry-btn"
                @click="openInquiryModal"
              >
                {{ $t('requestConsultation') }}
              </v-btn>
            </div>
          </div>
        </div>
      </v-col>
    </v-row>

    <v-row id="office" class="home__office mt-md-12 mb-12 mb-md-0" data-reveal>
      <v-col cols="12 my-10 d-none d-md-block">
        <p
          class="text-center text-neutra-bold mb-0 home__office--title"
        >
          {{ $t('whatIsFamilyOffice') }}
        </p>
      </v-col>
      <v-col cols="12">
        <v-row class="relative">
          <v-col
            cols="12"
            md="6"
            class="pr-md-0 serene-light-blue-bg pb-0 d-flex align-start"
          >
            <div class="px-md-10">
              <p
                class="text-neutra-bold home__office--title d-block d-md-none"
              >
                {{ $t('whatIsFamilyOffice') }}
              </p>
              <p class="home__office--description home__editorial-lead text-neutra-book mb-0 text-justify">
                {{ $t('familyOfficeDescription1') }}
              </p>
              <p class="home__office--description text-neutra-book mb-0 text-justify">
                {{ $t('familyOfficeDescription2') }}
              </p>
              <p class="home__office--description home__pull-quote text-neutra-book mb-0 text-justify">
                <b>{{ $t('familyOfficeDescription3') }}</b>
              </p>
            </div>
          </v-col>
          <v-col
            cols="12"
            md="6"
            class="pl-0 pr-md-4 py-0 relative"
          >
            <v-img
              :src="require('../assets/family.webp')"
              loading="lazy"
              class="home__office-family-image home__image-system home__image-system--feature"
              alt="family image"
            />
          </v-col>
        </v-row>
      </v-col>
      <v-col
        cols="11"
        md="5"
        class="d-flex justify-center align-start py-md-8 my-md-4"
      >
        <div class="pa-lg-12">
          <p class="text-neutra-bold home__office--bottom-text-left px-lg-8">
            {{ $t('familyOfficeSubLeft') }}
            {{ $t('familyOfficeSubLeftColor') }}?
          </p>
        </div>
      </v-col>
      <v-col
        cols="12"
        md="6"
        offset-md="1"
        class="d-flex justify-center align-start py-md-8 my-md-4"
      >
        <div class="d-flex flex-column justify-center pa-lg-10">
          <p class="text-neutra-book home__office--bottom-text-right home__editorial-lead text-justify mb-0">
            {{ $t('familyOfficeSubRight1') }}
          </p>
          <p class="text-neutra-book home__office--bottom-text-right text-justify mb-0">
            Parce que l’une des quêtes récurrentes de l’entrepreneur est la ma&icirc;trise de son
            temps, nous intervenons comme un guichet unique capable de l’aider à matr&icirc;ser son
            patrimoine personnel et professionnel.
          </p>
          <p class="text-neutra-book home__office--bottom-text-right text-justify mb-0">
            {{ $t('familyOfficeSubRight3') }}
          </p>
          <p class="text-neutra-book home__office--bottom-text-right home__pull-quote text-justify mb-0">
            {{ $t('familyOfficeSubRight4') }}
          </p>
        </div>
      </v-col>
    </v-row>

    <v-row id="founder" class="home__founder py-2 serene-light-yellow-bg relative" data-reveal>
      <v-col
        cols="12"
        md="3"
        class="d-flex justify-center align-center"
      >
        <v-img
          :src="require('../assets/founder.webp')"
          loading="lazy"
          class="home__founder--image home__image-system home__image-system--portrait"
          alt="selene"
        />
      </v-col>
      <v-col
        cols="12"
        md="9"
        class="home__founder-copy mt-10 mt-md-0"
      >
        <p class="home__founder--title text-neutra-bold mt-md-3">
          {{ $t('aboutFounder') }}
        </p>
        <p class="home__founder--serene home__editorial-lead text-neutra-book mb-0">
          <span v-html="$t('aboutSerene1')" />
        </p>
        <p class="home__founder--serene text-neutra-book mb-0">
          {{ $t('aboutSerene2') }}
        </p>
        <p class="home__founder--serene text-neutra-book mb-0">
          {{ $t('aboutSerene3') }}
        </p>
        <p class="home__founder--serene text-neutra-book mb-0">
          {{ $t('aboutSerene4') }}
        </p>
        <p class="home__founder--serene text-neutra-book mb-0">
          <span v-html="$t('aboutSerene5')" />
        </p>
        <div class="d-flex flex-row justify-center justify-md-start mt-2">
          <a
            :href="linkedIn"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span class="home__founder--link text-neutra-book">Profil LinkedIn</span>
          </a>
        </div>
      </v-col>
    </v-row>

    <v-row id="mission" class="home__mission" data-reveal>
      <v-col
        cols="12"
        md="10"
        offset-md="1"
        class="mt-1 mt-md-12 pt-12"
      >
        <p class="home__mission--title text-neutra-bold text-md-center">
          {{ $t('whatIsMission') }}
        </p>
        <v-row>
          <v-col
            cols="12"
            md="10"
            offset-md="1"
          >
            <p class="home__mission--description home__editorial-lead text-neutra-book px-md-10 text-justify">
              <span v-html="$t('missionDescription1')" />
            </p>
            <p class="home__mission--description text-neutra-book px-md-10 text-justify">
              <span v-html="$t('missionDescription2')" />
            </p>
            <p class="home__mission--description home__pull-quote text-neutra-book px-md-10 text-justify">
              <span v-html="$t('missionDescription3')" />
            </p>
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <v-row id="contact" class="home__contact" data-reveal>
      <v-col cols="12" md="10" offset-md="1" class="text-center py-12">
        <p class="home__contact--title text-neutra-bold mb-3">
          {{ $t('privateInquiryTitle') }}
        </p>
        <p class="home__contact--description text-neutra-book mb-8">
          {{ $t('privateInquirySubtitle') }}
        </p>
        <v-btn outlined class="home__inquiry-btn" @click="openInquiryModal">
          {{ $t('requestConsultation') }}
        </v-btn>
      </v-col>
    </v-row>

    <v-row class="home__footer pt-12 pb-8">
      <v-col cols="12" md="10" offset-md="1">
        <div class="home__legal-footer text-neutra-book">
          <p class="mb-2">{{ $t('legalAddress') }}</p>
          <p class="mb-2 text-numeric">{{ $t('legalSiret') }}</p>
          <div class="home__legal-links">
            <a
              href="https://consulting-huber.com/fr/"
              target="_blank"
              rel="noopener noreferrer"
              class="home__legal-external-link text-neutra-book"
            >
              Consulting
            </a>
            <button
              type="button"
              class="home__legal-client-access text-neutra-book"
              @click="openClientAccess"
            >
              {{ $t('clientAccess') }}
            </button>
          </div>
        </div>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { httpsCallable } from 'firebase/functions';
import { firebaseFunctions } from '../firebase';

export default {
  name: 'HomePage',
  data() {
    return {
      services: this.buildServices(),
      linkedIn: this.$i18n.t('linkedInLink'),
      isMenuOpen: false,
      isInquiryOpen: false,
      inquiryName: '',
      inquiryPhone: '',
      inquiryReferralCode: '',
      inquirySubmitting: false,
      inquiryFeedback: '',
    };
  },
  computed: {
    navigationLinks() {
      return [
        { id: 'office', label: this.$t('whatIsFamilyOffice') },
        { id: 'mission', label: this.$t('mission') },
        { id: 'founder', label: this.$t('founder') },
        { id: 'assetManagement', label: this.$t('assetManagement') },
        { id: 'legalAndTax', label: this.$t('legalAndTax') },
        { id: 'familySupport', label: this.$t('familySupport') },
      ];
    },
  },
  methods: {
    buildServices() {
      return [
        {
          id: 'assetManagement',
          badge: this.$i18n.t('serviceBadge1'),
          img: '/images/house.webp',
          description: {
            title: this.$i18n.t('serviceDescriptionTitle1'),
            subtitle1: this.$i18n.t('serviceDescriptionSubTitle1a'),
            subtitle2: this.$i18n.t('serviceDescriptionSubTitle1b'),
            subtitle3: this.$i18n.t('serviceDescriptionSubTitle1c'),
          },
        },
        {
          id: 'legalAndTax',
          badge: this.$i18n.t('serviceBadge2'),
          img: '/images/office.webp',
          description: {
            title: this.$i18n.t('serviceDescriptionTitle2'),
            subtitle1: this.$i18n.t('serviceDescriptionSubTitle2a'),
            subtitle2: this.$i18n.t('serviceDescriptionSubTitle2b'),
          },
        },
        {
          id: 'familySupport',
          badge: this.$i18n.t('serviceBadge3'),
          img: '/images/fireplace.webp',
          description: {
            title: this.$i18n.t('serviceDescriptionTitle3'),
            subtitle1: this.$i18n.t('serviceDescriptionSubTitle3a'),
            subtitle2: this.$i18n.t('serviceDescriptionSubTitle3b'),
            subtitle3: this.$i18n.t('serviceDescriptionSubTitle3c'),
          },
        },
      ];
    },
    navigateTo(id) {
      this.isMenuOpen = false;
      const target = document.getElementById(id);
      if (target) {
        const headerOffset = window.innerWidth >= 960 ? 150 : 205;
        const top = target.getBoundingClientRect().top + window.pageYOffset - headerOffset;
        window.scrollTo({ top, behavior: 'smooth' });
      }
    },
    openClientAccess() {
      this.isMenuOpen = false;
      window.location.hash = '#/client-access';
    },
    openInquiryModal() {
      this.isMenuOpen = false;
      this.isInquiryOpen = true;
      this.inquiryFeedback = '';
    },
    closeInquiryModal() {
      this.isInquiryOpen = false;
      this.inquiryFeedback = '';
    },
    async submitContactForm(payload) {
      const submitContact = httpsCallable(firebaseFunctions, 'submitContactForm');
      await submitContact(payload);
    },
    async submitInquiryForm() {
      this.inquiryFeedback = '';

      if (!this.inquiryName || !this.inquiryPhone) {
        this.inquiryFeedback = this.$t('privateInquiryValidation');
        return;
      }

      this.inquirySubmitting = true;

      try {
        await this.submitContactForm({
          type: 'private-inquiry',
          name: this.inquiryName,
          phone: this.inquiryPhone,
          referralCode: this.inquiryReferralCode,
          acceptedPrivacyPolicy: true,
        });

        this.inquiryFeedback = this.$t('privateInquirySuccess');
        this.inquiryName = '';
        this.inquiryPhone = '';
        this.inquiryReferralCode = '';
      } catch (error) {
        this.inquiryFeedback = this.$t('privateInquiryError');
      } finally {
        this.inquirySubmitting = false;
      }
    },
  },
};
</script>

<style lang="scss">
  .home {
    background: var(--bg-color);
    color: var(--text-color);
    padding-bottom: 5rem !important;
    overflow-x: hidden;
    --copy-size-desktop: 17px;
    --copy-size-mobile: 16px;
    --copy-line-height: 1.72;

    .v-row {
      margin-left: 0 !important;
      margin-right: 0 !important;
    }

    &__navbar {
      position: sticky;
      top: 0;
      z-index: 12;
      background: rgba(249, 248, 244, 0.94);
      backdrop-filter: blur(6px);
      padding: 4px 10px 8px;
      border-bottom: 1px solid $s-dirty-white;
    }

    &__navbar-controls {
      gap: 14px;
      min-height: 88px;
    }

    &__header-control {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 10px;
      border: 1px solid var(--accent);
      border-radius: 6px;
      height: 56px;
      padding: 0 26px;
      color: var(--text-color);
      letter-spacing: 0.08em;
      text-transform: uppercase;
      font-size: 14px;
      line-height: 1;
      white-space: nowrap;
      background: transparent;
      transition: border-color 220ms ease, background-color 220ms ease;
    }

    &__header-control:hover {
      border-color: var(--text-color);
      background-color: rgba(197, 160, 101, 0.08);
    }

    &__header-control--menu {
      min-width: 56px;
      width: 56px;
      padding: 0;
      order: 3;
    }

    &__header-control--vault {
      min-width: 56px;
      width: 56px;
      padding: 0;
      order: 2;
    }

    &__header-control--cta {
      min-width: 360px;
      order: 1;
    }

    &__menu-icon {
      display: inline-flex;
      flex-direction: column;
      justify-content: center;
      gap: 3px;
      width: 14px;

      span {
        display: block;
        width: 14px;
        height: 1px;
        background: currentColor;
      }
    }

    &__access-icon {
      position: relative;
      display: inline-block;
      width: 16px;
      height: 14px;
    }

    &__access-icon-shackle {
      position: absolute;
      top: 0;
      left: 4px;
      width: 8px;
      height: 6px;
      border: 1.5px solid currentColor;
      border-bottom: 0;
      border-radius: 8px 8px 0 0;
      box-sizing: border-box;
    }

    &__access-icon-body {
      position: absolute;
      left: 2px;
      top: 5px;
      width: 12px;
      height: 9px;
      border: 1.5px solid currentColor;
      border-radius: 2px;
      box-sizing: border-box;
    }

    &__drawer {
      z-index: 80 !important;
      position: fixed !important;
      top: 0 !important;
      max-height: 100vh !important;

      .v-navigation-drawer__content {
        background: #fffdf8;
      }
    }

    ::v-deep .v-navigation-drawer__scrim {
      z-index: 70 !important;
    }

    ::v-deep .home__drawer.v-navigation-drawer {
      top: 0 !important;
      z-index: 80 !important;
      max-height: 100vh !important;
    }

    &__drawer-link {
      display: block;
      width: 100%;
      text-align: left;
      padding: 10px 0;
      border: 0;
      border-bottom: 1px solid $s-dirty-white;
      background: transparent;
      color: var(--text-color);
      font-size: 18px;
    }

    &__drawer-client-access {
      display: inline-block;
      margin-top: 16px;
      color: var(--text-color);
      text-transform: uppercase;
      letter-spacing: 0.06em;
      font-size: 12px;
      border-bottom: 1px solid var(--accent);
      border-left: 0;
      border-right: 0;
      border-top: 0;
      background: transparent;
      padding: 0;
    }

    &__inquiry-btn {
      border-color: var(--accent) !important;
      color: var(--text-color) !important;
      border-width: 1px !important;
      border-radius: 6px !important;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      transition: transform 220ms ease, box-shadow 220ms ease, background-color 220ms ease !important;
    }

    &__inquiry-btn:hover {
      transform: translateY(-1px);
      box-shadow: 0 10px 20px rgba(11, 21, 33, 0.09);
      background-color: rgba(197, 160, 101, 0.08) !important;
    }

    &__icon-button {
      border: 0;
      background: transparent;
      color: var(--text-color);
      font-size: 26px;
      line-height: 1;
      padding: 0;
      cursor: pointer;
    }

    &__inquiry-modal {
      background: linear-gradient(180deg, var(--bg-color) 0%, #ffffff 65%);
      min-height: 100vh;
    }

    &__inquiry-modal-content {
      max-width: 560px;
      margin: 0 auto;
      padding: 28px 20px;

      @include respond-to(md) {
        padding: 60px 20px;
      }
    }

    &__inquiry-title {
      font-size: 30px;
    }

    &__inquiry-subtitle {
      font-size: 18px;
      max-width: 440px;
    }

    &__inquiry-field {
      margin-bottom: 22px;
    }

    &__inquiry-field-label {
      display: block;
      margin-bottom: 8px;
      font-size: 16px;
      letter-spacing: 0.01em;
      color: rgba(11, 21, 33, 0.9);
    }

    &__inquiry-input {
      display: block;
      width: 100%;
      border: 0;
      border-bottom: 1px solid rgba(11, 21, 33, 0.4);
      background: transparent;
      padding: 0 0 10px;
      font-size: 20px;
      line-height: 1.35;
      color: var(--text-color);
      transition: border-color 180ms ease;
    }

    &__inquiry-input:focus {
      border-bottom-color: var(--accent);
    }

    &__inquiry-input::placeholder {
      color: rgba(11, 21, 33, 0.35);
      opacity: 1;
    }

    &__inquiry-submit {
      border-color: var(--accent) !important;
      color: var(--text-color) !important;
    }

    &__navbar {
      &--logo {
        width: 230px;
        height: 90px;
        margin: 0 auto;
        @include respond-to(md) {
          width: 170px;
          height: 80px;
        }
      }
    }

    @media only screen and (min-width: 960px) {
      &__navbar-controls {
        justify-content: flex-end !important;
        flex-wrap: nowrap !important;
      }
    }

    @media only screen and (max-width: 959px) {
      &__navbar {
        padding: 4px 0 8px;

        .home__navbar-controls {
          min-height: initial;
          padding: 0 12px;
          gap: 8px;
        }

        .home__header-control--menu {
          display: block;
          width: 100%;
          order: 1;
        }

        .home__header-control--cta {
          width: 100%;
          min-width: 0;
          order: 2;
        }
      }

      &__service-editorial {
        grid-template-columns: 1fr;
        gap: 1rem;
        min-height: auto;
      }

      &__service-editorial-copy {
        margin-left: 0;
        padding: 0.5rem 0;
      }

      &__service-card {
        margin: 2rem 0 3rem;
      }

      &__service-card--image {
        min-height: 260px;
      }
    }

    &__title {
      font-size: 38px;
      margin-top: 2rem;
      @include respond-to(md) {
        font-size: 24px;
      }
    }

    #office,
    #founder,
    #mission,
    #contact,
    #assetManagement,
    #legalAndTax,
    #familySupport {
      scroll-margin-top: 150px;
    }

    &__hero {
      max-width: 980px;
      margin: 0.4rem auto 1.25rem;
      padding: 1.1rem 1rem 0.4rem;
      width: 100%;
    }

    &__hero-line {
      font-size: clamp(38px, 4.2vw, 58px);
      line-height: 1.06;
      letter-spacing: 0.01em;
      margin-left: auto;
      margin-right: auto;
      max-width: 14ch;
      overflow-wrap: normal;
      word-break: normal;
    }

    &__hero-subline {
      font-size: clamp(22px, 2.2vw, 34px);
      line-height: 1.32;
      letter-spacing: 0.02em;
      max-width: 24ch;
      margin-left: auto;
      margin-right: auto;
      text-wrap: pretty;
    }

    &__hero-kicker {
      text-transform: uppercase;
      letter-spacing: 0.2em;
      font-size: 11px;
      opacity: 0.75;
    }

    &__hero-description {
      max-width: 660px;
      margin-left: auto;
      margin-right: auto;
      font-size: var(--copy-size-desktop);
      line-height: var(--copy-line-height);
      letter-spacing: 0.01em;
      overflow-wrap: anywhere;
      text-wrap: pretty;
    }

    @media only screen and (max-width: 959px) {
      #office,
      #founder,
      #mission,
      #contact,
      #assetManagement,
      #legalAndTax,
      #familySupport {
        scroll-margin-top: 205px;
      }

      &__hero {
        margin: 0.4rem auto 1rem;
        padding: 0.85rem 0.3rem 0.2rem;
      }

      &__hero-kicker {
        font-size: 10px;
      }

      &__hero-line {
        font-size: clamp(18px, 8.6vw, 31px);
        line-height: 1.12;
        max-width: 14ch;
      }

      &__hero-subline {
        font-size: clamp(16px, 5.2vw, 23px);
        line-height: 1.3;
        max-width: 21ch;
      }

      &__hero-description {
        font-size: var(--copy-size-mobile);
        line-height: var(--copy-line-height);
        max-width: 92%;
      }

      &__inquiry-field-label {
        font-size: 14px;
      }

      &__inquiry-input {
        font-size: 18px;
        line-height: 1.3;
      }
    }

    &__services {
      padding-right: clamp(16px, 3vw, 56px) !important;

      &--title {
        font-size: 56px;
        letter-spacing: 0.02em;
      }
    }

    &__editorial-lead {
      font-size: clamp(18px, 1.2vw, 24px);
      line-height: 1.48;
      letter-spacing: 0.005em;
      color: rgba(11, 21, 33, 0.86);
      text-wrap: balance;

      @include respond-to(sm) {
        font-size: clamp(16px, 4.6vw, 20px);
      }
    }

    &__pull-quote {
      max-width: 48ch;
      padding: 0.25rem 0 0.35rem 16px;
      border-left: 2px solid var(--accent);
      font-family: 'Playfair Display', serif;
      font-style: italic;
      line-height: 1.55 !important;
      color: rgba(11, 21, 33, 0.94);

      @include respond-to(sm) {
        padding-left: 12px;
      }
    }

    &__image-system {
      overflow: hidden;
      box-shadow: 0 24px 52px rgba(11, 21, 33, 0.16);
      position: relative;
      isolation: isolate;

      .v-image__image {
        filter: saturate(0.82) contrast(0.92) brightness(0.93);
        transform: scale(1.02);
        transition: transform 520ms ease, filter 520ms ease;
      }

      &::before {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(135deg, rgba(249, 248, 244, 0.2), rgba(11, 21, 33, 0.22));
        pointer-events: none;
        z-index: 1;
      }
    }

    &__image-system--service,
    &__image-system--feature {
      border: 1px solid rgba(197, 160, 101, 0.55);
      border-radius: 4px;
    }

    &__image-system--portrait {
      border: 1px solid rgba(197, 160, 101, 0.45);
      box-shadow: 0 16px 36px rgba(11, 21, 33, 0.2);

      &::before {
        background: linear-gradient(180deg, rgba(249, 248, 244, 0.12), rgba(11, 21, 33, 0.1));
      }
    }

    &__service {
      &-card {
        margin: 3.5rem 0 5rem;
        &--badge {
          border: 1px solid var(--accent);
          color: var(--text-color);
          padding: 5px 10px;
          position: relative;
          display: inline-block;
          border-radius: 4px;
          letter-spacing: 0.03em;
          transition: border-color 220ms ease, background-color 220ms ease;
          background: rgba(249, 248, 244, 0.92);
          backdrop-filter: blur(2px);

          p {
            font-size: clamp(12px, 0.8vw, 14px);
            line-height: 1.1;
          }
        }
        &--badge-overlay {
          position: absolute;
          top: 16px;
          left: 16px;
          z-index: 4;
          margin-bottom: 0;

          @include respond-to(sm) {
            top: 10px;
            left: 10px;
          }
        }
        &--image {
          min-height: 100%;
          border-radius: 0 !important;
          background-size: cover;
          height: 100%;
          width: 100%;
          overflow: hidden;
          .v-image__image {
            background-position: center !important;
          }
        }
        &--title {
          margin-bottom: 0 !important;
          margin-top: 2px;
          font-size: 40px;
          line-height: 1.15;
          text-align: left;
          width: 100%;
        }
        &--subtitle {
          margin-bottom: 0 !important;
          font-size: var(--copy-size-desktop);
          line-height: var(--copy-line-height);
          text-align: left;
          max-width: 56ch;
          width: 100%;
          text-wrap: pretty;
          margin-top: 0;
          margin-bottom: 0.95em !important;
          color: rgba(11, 21, 33, 0.84);
        }
      }

      &-editorial {
        display: grid;
        grid-template-columns: minmax(300px, 42%) minmax(0, 1fr);
        gap: clamp(24px, 3vw, 44px);
        align-items: start;
        position: relative;
        min-height: 480px;
        transition: transform 300ms ease;

        @include respond-to(md) {
          grid-template-columns: 1fr;
          gap: 1.5rem;
        }
      }

      &-editorial-media {
        position: relative;
        z-index: 1;
        display: flex;
        overflow: hidden;
        min-height: 100%;
        align-self: start;
      }

      &-editorial-copy {
        position: relative;
        z-index: 2;
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        background: var(--bg-color);
        padding: 0;
        max-width: 840px;
        min-width: 0;

        @include respond-to(md) {
          padding: 1rem 0;
          max-width: 100%;
        }
      }

      &-editorial:hover .home__service-card--badge {
        background-color: rgba(197, 160, 101, 0.08);
        border-color: rgba(197, 160, 101, 0.9);
      }

      &-editorial:hover .home__service-card--image .v-image__image {
        transform: scale(1.06);
        filter: saturate(0.88) contrast(0.95) brightness(0.95);
      }
    }

    &__office {
      margin-top: 4.5rem !important;
      &-family-image {
        min-height: 440px;
        overflow: hidden;
      }
      &--title {
        font-size: clamp(44px, 3.5vw, 58px);
        line-height: 1.12;
        @include respond-to(sm) {
          font-size: 34px;
        }
      }
      &--description {
        font-size: var(--copy-size-desktop);
        line-height: var(--copy-line-height);
        max-width: 56ch;
        margin-bottom: 1em !important;
        text-align: left !important;
        text-wrap: pretty;
        @include respond-to(sm) {
          font-size: var(--copy-size-mobile);
          max-width: 100%;
        }
      }
      &--small-logo {
        position: absolute;
        left: 0;
        right: 0;
        margin: 0 auto;
        bottom: -45px;
      }
      &--bottom-text {
        &-right {
          font-size: var(--copy-size-desktop);
          line-height: var(--copy-line-height);
          max-width: 56ch;
          margin-bottom: 1em !important;
          text-align: left !important;
          text-wrap: pretty;
          @include respond-to(md) {
            font-size: var(--copy-size-mobile);
            max-width: 100%;
          }
        }
        &-left {
          font-size: 36px;
          line-height: 1.2;
          @include respond-to(md) {
            font-size: 22px;
          }
        }
      }
    }

    &__founder {
      padding-left: 16px;
      padding-right: 16px;

      @media only screen and (min-width: 960px) {
        padding-left: clamp(20px, 2.8vw, 44px);
        padding-right: clamp(20px, 2.8vw, 44px);
      }

      &-copy {
        border-left: 1px solid rgba(197, 160, 101, 0.45);
        padding-left: clamp(18px, 2vw, 30px);
        @media only screen and (min-width: 960px) {
          padding-right: clamp(28px, 5vw, 84px);
        }
      }

      &--image {
        width: 160px;
        height: 160px;
        margin: 0 auto;
        border-radius: 50%;
        flex: none !important;
        @include respond-to(sm) {
          position: absolute !important;
          width: 140px;
          height: 140px;
          left: 0;
          right: 0;
          margin: auto;
          top: -50px;
        }
      }
      &--title {
        font-size: 22px;
      }
      &--serene {
        font-size: clamp(18px, 1.08vw, 22px);
        text-align: left;
        line-height: 1.72;
        max-width: 60ch;
        margin-bottom: 0.95em !important;
        text-wrap: pretty;
        color: rgba(11, 21, 33, 0.88);
        @include respond-to(sm) {
          font-size: var(--copy-size-mobile);
          max-width: 100%;
        }
      }
      &--link {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        font-size: 13px;
        line-height: 1;
        letter-spacing: 0.03em;
        text-transform: none;
        border: 1px solid var(--accent);
        border-radius: 4px;
        padding: 10px 14px;
        color: var(--text-color);
        transition: border-color 180ms ease, background-color 180ms ease;
      }

      &--link:hover {
        border-color: var(--text-color);
        background-color: rgba(197, 160, 101, 0.08);
      }
    }

    &__mission {
      &--title {
        font-size: 54px;
        @include respond-to(sm) {
          font-size: 22px;
        }
      }
      &--description {
        font-size: clamp(18px, 1.02vw, 21px);
        line-height: 1.68;
        max-width: 60ch;
        margin-left: auto;
        margin-right: auto;
        text-align: left !important;
        text-wrap: pretty;
        margin-bottom: 1em !important;
        color: rgba(11, 21, 33, 0.84);
        @include respond-to(sm) {
          font-size: var(--copy-size-mobile);
          max-width: 100%;
        }
      }
    }

    &__contact {
      margin-top: 5rem;
      &--title {
        font-size: 50px;
      }

      &--description {
        font-size: var(--copy-size-desktop);
        line-height: var(--copy-line-height);
        letter-spacing: 0.01em;
      }
    }

    &__hero-description strong,
    &__service-card--subtitle strong,
    &__founder--serene strong,
    &__mission--description strong {
      font-weight: 500;
      color: var(--text-color);
    }

    &__client-access {
      &-card {
        border: 1px solid var(--accent);
        padding: 2rem 2.4rem;
        background: rgba(255, 255, 255, 0.6);
      }

      &-title {
        font-size: 44px;
      }

      &-subtitle {
        font-size: 18px;
      }
    }

    &__footer {
      border-top: 1px solid var(--accent);
      margin-top: 5rem;
      padding-top: 2rem;
    }

    &__legal-footer {
      max-width: 700px;
      margin: 0 auto;
      text-align: center;

      p {
        font-size: 12px;
        letter-spacing: 0.02em;
      }
    }

    &__legal-links {
      display: inline-flex;
      flex-direction: column;
      align-items: center;
      gap: 8px;
    }

    &__legal-client-access {
      color: var(--text-color);
      text-transform: uppercase;
      letter-spacing: 0.08em;
      font-size: 11px;
      border-bottom: 1px solid var(--accent);
      border-left: 0;
      border-right: 0;
      border-top: 0;
      background: transparent;
      padding: 0;
      transition: border-color 180ms ease, opacity 180ms ease;
    }

    &__legal-client-access:hover {
      border-bottom-color: var(--text-color);
      opacity: 0.9;
    }

    &__legal-external-link {
      display: inline-block;
      color: var(--text-color);
      font-size: 12px;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      border-bottom: 1px solid var(--accent);
      padding-bottom: 2px;
      transition: border-color 180ms ease, opacity 180ms ease;
    }

    &__legal-external-link:hover {
      border-bottom-color: var(--text-color);
      opacity: 0.9;
    }
  }
</style>
